import React from 'react';
import { cn } from '@cotera/client/v0/lib/utils';

/**
 * How many trailing characters of a field name are kept when the row is too
 * narrow for all of it.
 *
 * Long enough to separate names that differ only at the end, which is the
 * common shape here: a step's outputs share a prefix, so
 * `KEY_CONTACTS_FOUND` and `KEY_CONTACTS_COUNT` both truncate to
 * `KEY_CONTA…` and the list becomes four identical rows.
 */
const NAME_TAIL_CHARS = 9;

/**
 * A field name split so the end of it survives truncation.
 *
 * The head takes whatever room is left and ellipses when it runs out; the tail
 * never shrinks. With room for the whole name the two render flush and read as
 * one string, so nothing is spent on names that already fit.
 */
export const splitFieldName = (
  leaf: string
): { head: string; tail: string } => {
  // Only a name with nothing left over to keep. How much of a name actually
  // fits depends on the row's width, not on its length, so anything longer than
  // the tail is split: with room to spare the two halves render flush and the
  // split costs nothing.
  if (leaf.length <= NAME_TAIL_CHARS) {
    return { head: leaf, tail: '' };
  }
  return {
    head: leaf.slice(0, leaf.length - NAME_TAIL_CHARS),
    tail: leaf.slice(leaf.length - NAME_TAIL_CHARS),
  };
};

/**
 * The inner content of a variable-picker row: a tree-indented leaf name on the
 * left, then (right-aligned) a muted value/type hint and — when fields come from
 * more than one step — the source node's name so identically-named fields stay
 * attributable. Shared by both pickers — the `/` suggestion menu and the
 * non-string bolt escape hatch — so they render identically inside whatever
 * clickable wrapper each uses.
 */
export const VariableFieldRow: React.FC<{
  leaf: string;
  depth: number;
  /** Pre-formatted type hint (see `describeType`). */
  hint: string;
  hintMono: boolean;
  /** Source node/agent label; rendered as a chip on the far right when present. */
  source?: string;
}> = ({ leaf, depth, hint, hintMono, source }) => {
  const hasHint = hint !== '';
  const hasSource = source !== undefined && source !== '';
  const { head, tail } = splitFieldName(leaf);
  return (
    <>
      {/* Titled, because the name is the one thing in the row that had no way
          to be read in full: the hint and the source chip carried a `title`
          and the name did not, so a truncated field could only be identified
          by inserting it and reading what came out. */}
      <span
        title={leaf}
        className="flex min-w-0 flex-1 items-center"
        style={{ paddingLeft: Math.min(depth, 5) * 12 }}
      >
        {depth > 0 ? (
          <span className="text-muted-foreground/40 mr-1 shrink-0">↳</span>
        ) : null}
        <span className="min-w-0 truncate">{head}</span>
        {tail === '' ? null : (
          <span className="shrink-0 whitespace-pre">{tail}</span>
        )}
      </span>
      {hasHint || hasSource ? (
        // Bounded so a long agent name truncates here rather than crushing the
        // field name on the left, which is the half a reader is choosing by.
        // Both children truncate with the full value/name on hover via `title`.
        <span className="flex min-w-0 max-w-[45%] shrink items-center justify-end gap-2">
          {hasHint ? (
            <span
              title={hint}
              className={cn(
                'text-muted-foreground min-w-0 shrink truncate text-[11px]',
                hintMono && 'font-mono'
              )}
            >
              {hint}
            </span>
          ) : null}
          {hasSource ? (
            <span
              title={source}
              className="bg-muted/60 text-muted-foreground min-w-0 shrink truncate rounded px-1.5 py-0.5 text-[10px] font-medium"
            >
              {source}
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );
};
